import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, PageHeader, Toggle } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { PermissionGate } from "@/components/PermissionGate";
import {
  useArchiveContentTemplate,
  useContentTemplate,
  useCreateContentTemplate,
  useUpdateContentTemplate,
  type CreateContentTemplatePayload,
} from "@/hooks/useContentTemplates";
import type {
  AudienceRole,
  ContentType,
  InputField,
  Locale,
  ModelOverrides,
  TemplateFull,
  TemplateStatus,
} from "@/types/content";

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SYSTEM_PROMPT_MAX_BYTES = 8 * 1024;

const CONTENT_TYPES: { value: ContentType; label: string }[] = [
  { value: "product_caption", label: "Product caption" },
  { value: "motivational_nudge", label: "Motivational nudge" },
  { value: "voice_message", label: "Voice message" },
  { value: "gamified_prompt", label: "Gamified prompt" },
];

const LOCALES: { value: Locale; label: string }[] = [
  { value: "ph", label: "Philippines (ph)" },
  { value: "id", label: "Indonesia (id)" },
  { value: "uae", label: "UAE (uae)" },
  { value: "usa", label: "USA (usa)" },
  { value: "hk", label: "Hong Kong (hk)" },
  { value: "cy", label: "Cyprus (cy)" },
  { value: "global", label: "Global (global)" },
];

const AUDIENCES: { value: AudienceRole; label: string }[] = [
  { value: "guest", label: "Guest" },
  { value: "business_owner", label: "Business Owner" },
  { value: "preferred_member", label: "Preferred Member" },
  { value: "product_center", label: "Product Center" },
  { value: "branch_operator", label: "Branch Operator" },
  { value: "admin", label: "Admin" },
];

const TTS_VOICES = ["alloy", "nova", "onyx", "shimmer", "echo", "fable"] as const;

const STATUS_OPTIONS: { value: TemplateStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

const STATUS_BADGE: Record<
  TemplateStatus,
  { variant: "success" | "warning" | "default"; label: string }
> = {
  draft: { variant: "warning", label: "Draft" },
  active: { variant: "success", label: "Active" },
  archived: { variant: "default", label: "Archived" },
};

const INPUT_FIELD_HELP =
  'Array of input field definitions, e.g. [{ "name": "product_name", "label": "Product name", "type": "string", "required": true, "max_len": 80 }]. Allowed types: string, text, number, enum (with "enum": ["a","b"]).';

interface FormState {
  title: string;
  slug: string;
  content_type: ContentType;
  locale: Locale;
  audience_role: AudienceRole;
  description: string;
  system_prompt: string;
  input_schema_text: string;
  override_chat_model: string;
  override_temperature: string;
  override_max_tokens: string;
  use_rag: boolean;
  rag_context: string;
  tts_voice: string;
  status: TemplateStatus;
}

const EMPTY_INPUT_SCHEMA = "[]";

const createEmptyForm = (): FormState => ({
  title: "",
  slug: "",
  content_type: "product_caption",
  locale: "ph",
  audience_role: "business_owner",
  description: "",
  system_prompt: "",
  input_schema_text: EMPTY_INPUT_SCHEMA,
  override_chat_model: "",
  override_temperature: "",
  override_max_tokens: "",
  use_rag: false,
  rag_context: "",
  tts_voice: "",
  status: "draft",
});

const formFromTemplate = (tpl: TemplateFull): FormState => ({
  title: tpl.title,
  slug: tpl.slug,
  content_type: tpl.content_type,
  locale: tpl.locale,
  audience_role: tpl.audience_role,
  description: tpl.description ?? "",
  system_prompt: tpl.system_prompt,
  input_schema_text: JSON.stringify(tpl.input_schema ?? [], null, 2),
  override_chat_model: tpl.model_overrides?.chat_model ?? "",
  override_temperature:
    tpl.model_overrides?.temperature !== undefined &&
    tpl.model_overrides?.temperature !== null
      ? String(tpl.model_overrides.temperature)
      : "",
  override_max_tokens:
    tpl.model_overrides?.max_tokens !== undefined &&
    tpl.model_overrides?.max_tokens !== null
      ? String(tpl.model_overrides.max_tokens)
      : "",
  use_rag: tpl.use_rag,
  rag_context: tpl.rag_context ?? "",
  tts_voice: tpl.tts_voice ?? "",
  status: tpl.status,
});

interface ParsedSchema {
  ok: boolean;
  value: InputField[];
  error: string | null;
}

const parseInputSchema = (raw: string): ParsedSchema => {
  const text = raw.trim();
  if (!text) {
    return { ok: true, value: [], error: null };
  }
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) {
      return { ok: false, value: [], error: "Input schema must be a JSON array." };
    }
    for (let i = 0; i < parsed.length; i += 1) {
      const f = parsed[i];
      if (!f || typeof f !== "object") {
        return {
          ok: false,
          value: [],
          error: `Field #${i + 1} must be an object.`,
        };
      }
      if (typeof f.name !== "string" || !f.name) {
        return {
          ok: false,
          value: [],
          error: `Field #${i + 1} requires a non-empty "name".`,
        };
      }
      if (!["string", "text", "number", "enum"].includes(f.type)) {
        return {
          ok: false,
          value: [],
          error: `Field "${f.name}" has invalid type "${f.type}". Allowed: string, text, number, enum.`,
        };
      }
      if (typeof f.required !== "boolean") {
        return {
          ok: false,
          value: [],
          error: `Field "${f.name}" requires "required": boolean.`,
        };
      }
    }
    return { ok: true, value: parsed as InputField[], error: null };
  } catch (err) {
    return {
      ok: false,
      value: [],
      error: `Invalid JSON: ${(err as Error).message}`,
    };
  }
};

const buildModelOverrides = (
  form: FormState
): ModelOverrides | null => {
  const overrides: ModelOverrides = {};
  let hasAny = false;
  if (form.override_chat_model.trim()) {
    overrides.chat_model = form.override_chat_model.trim();
    hasAny = true;
  }
  if (form.override_temperature.trim()) {
    const t = Number(form.override_temperature);
    if (!Number.isNaN(t)) {
      overrides.temperature = t;
      hasAny = true;
    }
  }
  if (form.override_max_tokens.trim()) {
    const m = Number(form.override_max_tokens);
    if (!Number.isNaN(m)) {
      overrides.max_tokens = m;
      hasAny = true;
    }
  }
  return hasAny ? overrides : null;
};

const ContentTemplateEditor = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const isNew = !routeId || routeId === "new";
  const numericId = isNew ? ("new" as const) : Number(routeId);

  const { data: loaded, isLoading } = useContentTemplate(numericId);
  const createMutation = useCreateContentTemplate();
  const updateMutation = useUpdateContentTemplate();
  const archiveMutation = useArchiveContentTemplate();

  const [form, setForm] = useState<FormState>(createEmptyForm);
  const [hydrated, setHydrated] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [showOverrides, setShowOverrides] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Hydrate the local edit copy from the loaded template once. We don't re-sync
  // on every refetch — that would clobber the user's in-flight edits.
  useEffect(() => {
    if (!isNew && loaded && !hydrated) {
      setForm(formFromTemplate(loaded));
      const overrides = loaded.model_overrides;
      if (
        overrides &&
        (overrides.chat_model || overrides.temperature !== null || overrides.max_tokens !== null)
      ) {
        setShowOverrides(true);
      }
      setHydrated(true);
    }
  }, [loaded, isNew, hydrated]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const systemPromptBytes = useMemo(
    () => new Blob([form.system_prompt]).size,
    [form.system_prompt]
  );
  const systemPromptOverLimit = systemPromptBytes > SYSTEM_PROMPT_MAX_BYTES;

  const slugInvalid = form.slug.length > 0 && !SLUG_PATTERN.test(form.slug);

  const canSave =
    Boolean(form.title.trim()) &&
    Boolean(form.slug.trim()) &&
    !slugInvalid &&
    Boolean(form.system_prompt.trim()) &&
    !systemPromptOverLimit &&
    !schemaError;

  const handleResetToLoaded = () => {
    if (!loaded) return;
    setForm(formFromTemplate(loaded));
    setSchemaError(null);
    setSubmitError(null);
  };

  const handleCancel = () => {
    navigate("/content-templates");
  };

  const buildPayload = (): CreateContentTemplatePayload | null => {
    const parsed = parseInputSchema(form.input_schema_text);
    if (!parsed.ok) {
      setSchemaError(parsed.error);
      return null;
    }
    setSchemaError(null);
    return {
      slug: form.slug.trim(),
      content_type: form.content_type,
      locale: form.locale,
      audience_role: form.audience_role,
      title: form.title.trim(),
      description: form.description.trim() || null,
      system_prompt: form.system_prompt,
      input_schema: parsed.value,
      model_overrides: buildModelOverrides(form),
      use_rag: form.use_rag,
      rag_context: form.use_rag ? form.rag_context.trim() || null : null,
      tts_voice:
        form.content_type === "voice_message" && form.tts_voice
          ? form.tts_voice
          : null,
      status: form.status,
    };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    const payload = buildPayload();
    if (!payload) return;
    try {
      if (isNew) {
        await createMutation.mutateAsync(payload);
      } else {
        await updateMutation.mutateAsync({ id: Number(routeId), ...payload });
      }
      navigate("/content-templates");
    } catch (err) {
      const detail = (err as { detail?: string; message?: string }) ?? {};
      setSubmitError(detail.detail || detail.message || "Failed to save template.");
    }
  };

  const handleArchive = async () => {
    if (isNew || !loaded) return;
    const ok = window.confirm(
      `Archive "${loaded.title}"? Active callers will fall back to the next active template.`
    );
    if (!ok) return;
    try {
      await archiveMutation.mutateAsync(loaded.id);
      navigate("/content-templates");
    } catch (err) {
      const detail = (err as { detail?: string; message?: string }) ?? {};
      setSubmitError(detail.detail || detail.message || "Failed to archive.");
    }
  };

  const headingTitle = isNew
    ? "New template"
    : loaded
      ? `Edit: ${loaded.title}`
      : "Edit template";

  if (!isNew && isLoading && !loaded) {
    return (
      <div className="page">
        <PageHeader label="Content Generation" title="Loading template..." />
        <div className="card">
          <div className="card-b">
            <p className="meta" style={{ textAlign: "center", padding: "24px 0" }}>
              Loading...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const saveGateAction = isNew ? "create" : "edit";
  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    archiveMutation.isPending;

  const statusBadge = STATUS_BADGE[form.status];

  return (
    <PermissionGate
      resource="content_templates"
      action="view"
      fallback={
        <div className="page">
          <PageHeader label="Content Generation" title={headingTitle} />
          <div className="card">
            <div className="card-b">
              <p className="meta" style={{ textAlign: "center", padding: "24px 0" }}>
                You do not have permission to view content templates.
              </p>
            </div>
          </div>
        </div>
      }
    >
      <div className="page">
        <PageHeader
          label="Content Generation"
          title={headingTitle}
          description={
            isNew
              ? "Define a new prompt template. The content type, locale, audience role, and slug form a unique key and cannot be changed after create."
              : "Edit the prompt body, input schema, and generation overrides. The unique key is locked."
          }
          actions={
            !isNew ? (
              <Badge variant={statusBadge.variant} className="lg">
                <span className="dot" />
                {statusBadge.label}
              </Badge>
            ) : undefined
          }
        />

        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-b">
              {/* 1 · IDENTITY */}
              <div className="sec">
                <div className="sec-h">
                  <b>
                    <span className="sec-n">1</span>Identity
                  </b>
                  <span>The unique key. Locked after create.</span>
                </div>

                <div className="row c2">
                  <div className="fg">
                    <label className="fl">
                      Title <span className="req">*</span>
                    </label>
                    <input
                      className="inp"
                      value={form.title}
                      onChange={(e) => updateField("title", e.target.value)}
                      placeholder="e.g. Product launch caption"
                      required
                    />
                  </div>
                  <div className="fg">
                    <label className="fl">
                      Slug <span className="req">*</span>
                    </label>
                    <input
                      className="inp mono"
                      style={slugInvalid ? { borderColor: "var(--err)" } : undefined}
                      value={form.slug}
                      onChange={(e) =>
                        updateField("slug", e.target.value.toLowerCase())
                      }
                      placeholder="lowercase-with-dashes"
                      pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
                      disabled={!isNew}
                      required
                    />
                    <p className={"hint" + (slugInvalid ? " err" : "")}>
                      {slugInvalid
                        ? "Slug must be lowercase letters, digits and dashes only."
                        : isNew
                          ? "Lowercase letters, digits, and dashes only. Locked after create."
                          : "Locked after create."}
                    </p>
                  </div>
                </div>

                <div className="row c3">
                  <div className="fg">
                    <label className="fl">
                      Content type <span className="req">*</span>
                    </label>
                    <select
                      className="sel"
                      value={form.content_type}
                      onChange={(e) =>
                        updateField("content_type", e.target.value as ContentType)
                      }
                      disabled={!isNew}
                    >
                      {CONTENT_TYPES.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="fg">
                    <label className="fl">
                      Locale <span className="req">*</span>
                    </label>
                    <select
                      className="sel"
                      value={form.locale}
                      onChange={(e) =>
                        updateField("locale", e.target.value as Locale)
                      }
                      disabled={!isNew}
                    >
                      {LOCALES.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="fg">
                    <label className="fl">
                      Audience role <span className="req">*</span>
                    </label>
                    <select
                      className="sel"
                      value={form.audience_role}
                      onChange={(e) =>
                        updateField(
                          "audience_role",
                          e.target.value as AudienceRole
                        )
                      }
                      disabled={!isNew}
                    >
                      {AUDIENCES.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="fg">
                  <label className="fl">
                    Description <span className="opt">(optional)</span>
                  </label>
                  <textarea
                    className="ta"
                    rows={3}
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    placeholder="What this template is for, who calls it, what tone to expect."
                  />
                </div>
              </div>

              {/* 2 · SYSTEM PROMPT */}
              <div className="sec">
                <div className="sec-h">
                  <b>
                    <span className="sec-n">2</span>System prompt
                  </b>
                  <span>Written as the instruction body sent to the model.</span>
                </div>
                <div className="fg">
                  <div className="lbl-row">
                    <label className="fl">Prompt</label>
                    <span className={"cnt" + (systemPromptOverLimit ? " warn" : "")}>
                      {systemPromptBytes.toLocaleString()} bytes /{" "}
                      {SYSTEM_PROMPT_MAX_BYTES.toLocaleString()}
                    </span>
                  </div>
                  <textarea
                    className="ta mono"
                    rows={14}
                    value={form.system_prompt}
                    onChange={(e) => updateField("system_prompt", e.target.value)}
                    placeholder="Write a launch caption for {{product_name}} aimed at {{audience}}..."
                    required
                  />
                  <p className="hint">
                    Use {"{{placeholders}}"} for input fields.
                  </p>
                </div>
              </div>

              {/* 3 · INPUT SCHEMA */}
              <div className="sec">
                <div className="sec-h">
                  <b>
                    <span className="sec-n">3</span>Input schema
                  </b>
                  <span>Defines the form shown in Test Run and to API callers.</span>
                </div>
                <div className="fg">
                  <div className="lbl-row">
                    <label className="fl">Input schema (JSON)</label>
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {schemaError ? (
                        <Badge variant="error">
                          <span className="dot" />
                          Invalid
                        </Badge>
                      ) : (
                        <Badge variant="success">
                          <span className="dot" />
                          Valid
                        </Badge>
                      )}
                      <span className="act-txt" title={INPUT_FIELD_HELP}>
                        Help: input field shape
                      </span>
                    </span>
                  </div>
                  <textarea
                    className="ta mono"
                    style={schemaError ? { borderColor: "var(--err)" } : undefined}
                    rows={8}
                    value={form.input_schema_text}
                    onChange={(e) =>
                      updateField("input_schema_text", e.target.value)
                    }
                    // Parse on blur — typing JSON character-by-character would flash
                    // spurious errors on every keystroke.
                    onBlur={() => {
                      const parsed = parseInputSchema(form.input_schema_text);
                      setSchemaError(parsed.ok ? null : parsed.error);
                    }}
                    placeholder='[{"name":"product_name","type":"string","required":true,"max_len":80}]'
                  />
                  {schemaError ? (
                    <p className="hint err">{schemaError}</p>
                  ) : (
                    <p className="hint">
                      Each field: {`{name, label?, type, required, max_len?, enum?}`}. Parsed on blur.
                    </p>
                  )}
                </div>
              </div>

              {/* 4 · MODEL OVERRIDES */}
              <div className="sec">
                <div className="sec-h" style={{ display: "flex", alignItems: "flex-start" }}>
                  <div>
                    <b>
                      <span className="sec-n">4</span>Model overrides
                    </b>
                    <span>Leave off to inherit the defaults from Model Settings.</span>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowOverrides((v) => !v)}
                    style={{ marginLeft: "auto" }}
                  >
                    {showOverrides ? "Hide" : "Show"} model overrides
                  </Button>
                </div>
                {showOverrides && (
                  <div className="row c3">
                    <div className="fg">
                      <label className="fl">Chat model</label>
                      <input
                        className="inp"
                        value={form.override_chat_model}
                        onChange={(e) =>
                          updateField("override_chat_model", e.target.value)
                        }
                        placeholder="e.g. gpt-4o-mini"
                      />
                    </div>
                    <div className="fg">
                      <label className="fl">Temperature</label>
                      <input
                        type="number"
                        min={0}
                        max={2}
                        step={0.1}
                        className="inp num"
                        value={form.override_temperature}
                        onChange={(e) =>
                          updateField("override_temperature", e.target.value)
                        }
                        placeholder="0.0 – 2.0"
                      />
                    </div>
                    <div className="fg">
                      <label className="fl">Max tokens</label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        className="inp num"
                        value={form.override_max_tokens}
                        onChange={(e) =>
                          updateField("override_max_tokens", e.target.value)
                        }
                        placeholder="e.g. 400"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* 5 · RETRIEVAL (RAG) */}
              <div className="sec">
                <div className="sec-h">
                  <b>
                    <span className="sec-n">5</span>Retrieval (RAG)
                  </b>
                  <span>Pull grounding passages into the prompt before generating.</span>
                </div>
                <div className="subtle-box">
                  <div className="tg-row" style={{ paddingTop: 0 }}>
                    <span className="tx">
                      <b>Use retrieval</b>
                      <span>
                        When enabled, the generator pulls grounding snippets from the named context before composing the output.
                      </span>
                    </span>
                    <Toggle
                      checked={form.use_rag}
                      onChange={(v) => updateField("use_rag", v)}
                    />
                  </div>
                  <div
                    className="fg"
                    style={{
                      marginBottom: 0,
                      paddingTop: 11,
                      borderTop: "1px solid var(--line)",
                    }}
                  >
                    <label className="fl">RAG context name</label>
                    <input
                      className="inp mono"
                      value={form.rag_context}
                      onChange={(e) => updateField("rag_context", e.target.value)}
                      disabled={!form.use_rag}
                      placeholder="e.g. product_catalogue"
                    />
                  </div>
                </div>
              </div>

              {/* 6 · DELIVERY */}
              <div className="sec">
                <div className="sec-h">
                  <b>
                    <span className="sec-n">6</span>Delivery
                  </b>
                  <span>Voice and lifecycle.</span>
                </div>
                <div className="row c2">
                  {form.content_type === "voice_message" && (
                    <div className="fg">
                      <label className="fl">TTS voice</label>
                      <select
                        className="sel"
                        value={form.tts_voice}
                        onChange={(e) => updateField("tts_voice", e.target.value)}
                      >
                        <option value="">(use default)</option>
                        {TTS_VOICES.map((voice) => (
                          <option key={voice} value={voice}>
                            {voice}
                          </option>
                        ))}
                      </select>
                      <p className="hint">
                        Only applies to <b>Voice message</b> templates.
                      </p>
                    </div>
                  )}

                  <div className="fg">
                    <label className="fl">Status</label>
                    <select
                      className="sel"
                      value={form.status}
                      onChange={(e) =>
                        updateField("status", e.target.value as TemplateStatus)
                      }
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="hint">
                      Only Active templates are callable by the generation API.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {submitError && (
              <div className="card-b" style={{ borderTop: "1px solid var(--line)" }}>
                <div className="hint err">{submitError}</div>
              </div>
            )}

            <div className="card-f">
              <span className="meta">
                {!isNew && loaded ? (
                  <>
                    Last updated {new Date(loaded.updated_at).toLocaleString()}
                    {loaded.updated_by ? ` by ${loaded.updated_by}` : ""}
                  </>
                ) : (
                  "New unsaved template."
                )}
              </span>
              <div className="r">
                <Button type="button" variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                {!isNew && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleResetToLoaded}
                    disabled={!loaded || isMutating}
                  >
                    Reset to loaded
                  </Button>
                )}
                {!isNew && (
                  <PermissionGate resource="content_templates" action="delete">
                    <Button
                      type="button"
                      variant="danger"
                      onClick={handleArchive}
                      loading={archiveMutation.isPending}
                    >
                      <Icon name="arch" />
                      Archive
                    </Button>
                  </PermissionGate>
                )}
                <PermissionGate
                  resource="content_templates"
                  action={saveGateAction}
                >
                  <Button
                    type="submit"
                    loading={createMutation.isPending || updateMutation.isPending}
                    disabled={!canSave}
                  >
                    {isNew ? "Create template" : "Save changes"}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </form>
      </div>
    </PermissionGate>
  );
};

export default ContentTemplateEditor;
